import type { LocaleCode, OperationCategory } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { categoryLabel } from '../operationCategoryMeta';

/**
 * The category management screen's view model and its presentational config.
 *
 * Everything here is pure: the page hook derives through it, the View renders,
 * and nothing in between reaches back to the API shapes.
 *
 * **There is no label map and there cannot be one.** The set is seeded per
 * tenant and then theirs — renamed, added to, removed — so a category's names
 * arrive from the server as data and this file only decides how to present
 * them. That is also why nothing here branches on a known key.
 */

/** A category as the table renders it. */
export type CategoryRow = {
    id: string;
    /** The name in the reader's language, falling back to the other. */
    label: string;
    /** The other language's name, or empty when the tenant filled only one. */
    secondaryLabel: string;
    nameKa: string;
    nameEn: string;
};

/**
 * Rows in the server's display order, which is the tenant's own.
 *
 * Never re-sorted here: the default set has a shape — the everyday kinds first,
 * the residual last — that neither language's alphabet preserves, and a new
 * category lands where the server put it.
 */
export const toCategoryRows = (
    categories: readonly OperationCategory[],
    locale: LocaleCode,
): CategoryRow[] =>
    categories.map((category) => {
        const primary = locale === 'ka' ? category.nameKa : category.nameEn;
        const secondary = locale === 'ka' ? category.nameEn : category.nameKa;
        return {
            id: category.id,
            label: categoryLabel(category, locale),
            // Empty when the tenant filled only one language, and also when the
            // fallback already promoted the other name into `label` — showing
            // the same string twice would read as a duplicate rather than as a
            // translation.
            secondaryLabel: primary ? secondary : '',
            nameKa: category.nameKa,
            nameEn: category.nameEn,
        };
    });

/**
 * What the name form is editing: a new category, or an existing one.
 *
 * One form for both, because the fields are identical and the only difference
 * is the verb on the button. `null` means the form is closed.
 */
export type CategoryFormState = {
    /** `null` when adding. The id being renamed otherwise. */
    id: string | null;
    nameKa: string;
    nameEn: string;
};

export const EMPTY_CATEGORY_FORM: CategoryFormState = {
    id: null,
    nameKa: '',
    nameEn: '',
};

/**
 * Whether the form may be submitted.
 *
 * **Either name is enough**, matching the server: a tenant working in one
 * language leaves the other empty and every screen falls back, so demanding both
 * would refuse the ordinary case. Only the row with neither is refused, and it
 * is refused here as a disabled button rather than as a round trip.
 */
export const isCategoryFormSubmittable = (form: CategoryFormState): boolean =>
    form.nameKa.trim().length > 0 || form.nameEn.trim().length > 0;

/** The keys the form's heading and confirm button read, by mode. */
export const categoryFormCopy = (form: CategoryFormState) =>
    form.id === null
        ? {
              headingKey: 'operations.categories.form.addTitle',
              confirmKey: 'operations.categories.form.add',
          }
        : {
              headingKey: 'operations.categories.form.renameTitle',
              confirmKey: 'operations.categories.form.rename',
          };

/**
 * The server's refusal, rendered as the server wrote it.
 *
 * **Not translated, and that is the same trade the editor documents.** The
 * blocked-delete message names the operations standing in the way — "Payroll is
 * still used by the operations "Bonus", "Advance" and 2 more" — and a
 * translated string could not carry names the client does not hold. The screen
 * gates on its own rules first, so what reaches here is the refusal that got
 * past them.
 *
 * `null` when the failure is not something the user can act on — a network
 * error, a 500 — which the caller renders as its own generic copy instead.
 */
export const refusalMessage = (
    error: ErrorResponse | undefined,
): string | null => {
    if (!error) {
        return null;
    }
    const message = error.message?.trim() ?? '';
    if (!message) {
        return null;
    }
    // 4xx only. A 500's message is an implementation detail, and a network
    // failure has none worth showing.
    return error.status >= 400 && error.status < 500 ? message : null;
};

/**
 * How many operations are blocking a delete, when the refusal carried a count.
 *
 * `null` when it did not — every other refusal, including "this is the last
 * category". The message stands on its own either way; this is only what lets
 * the screen say "3 operations" in its own words beside it.
 */
export const blockingOperationCount = (
    error: ErrorResponse | undefined,
): number | null => {
    const count = error?.dependents?.operationTemplates;
    return typeof count === 'number' && count > 0 ? count : null;
};
